import { useEffect, useState } from "react";
import { Modal } from "antd";
import { useQuery } from "react-query";
import debounce from "lodash.debounce";

import { getGoPeopleCollaborators } from "../../api";
import _ from "../../i18n";
import { formatPhone } from "../../utils";

import Button from "../common/Button";
import Loader from "../common/Loader";
import Pagination from "../common/Pagination";
import modalStyles from "./FiduciaryModal.module.scss";
import styles from "./ManageFiduciary.module.scss";

const LIMIT = 10;

// Le back GoPeople renvoie les rôles à part de la liste des users : on les
// rattache à leur user (même logique que prepareDataGoPeople dans
// Join/Collaborator/CollaboratorList.js).
const prepareDataGoPeople = (data) => {
  return data && data.users && data.users.length > 0
    ? data.users.map((item) => {
        const role = data.roles?.filter(
          (cont) => cont.user_id === item.uuid
        )[0];
        return { ...item, role };
      })
    : [];
};

const CollaboratorsModal = ({ modalOpen, setModalOpen, fiduciary }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const organizationId = fiduciary?.id;

  // Chaque ouverture (ou changement de fiduciaire) repart de la page 1 sans
  // garder la recherche précédente.
  useEffect(() => {
    if (modalOpen) {
      setCurrentPage(1);
      setSearch("");
    }
  }, [modalOpen, organizationId]);

  const { isLoading, isError, data } = useQuery(
    ["fiduciaryCollaborators", organizationId, currentPage, search],
    async () => {
      const response = await getGoPeopleCollaborators(
        organizationId,
        LIMIT,
        currentPage,
        search
      );
      return {
        collaborators: prepareDataGoPeople(response.data),
        total: response.data.total_count ?? 0,
      };
    },
    { enabled: Boolean(modalOpen && organizationId) }
  );

  const debouncedSearch = debounce((value) => {
    setCurrentPage(1);
    setSearch(value);
  }, 1000);

  if (!fiduciary) {
    return null;
  }

  const closeModal = (e) => {
    e && e.stopPropagation();
    setModalOpen(false);
  };

  const renderBody = () => {
    if (!organizationId) {
      return (
        <tr>
          <td colSpan={5} className={styles.centerCell}>
            Cette fiduciaire n'a pas d'organisation GoPeople associée
          </td>
        </tr>
      );
    }

    if (isLoading) {
      return (
        <tr>
          <td colSpan={5} className={styles.centerCell}>
            <Loader style={{ height: "10px" }} color={"#B2BCC6"} />
          </td>
        </tr>
      );
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={5} className={styles.centerCell}>
            {_("error")}
          </td>
        </tr>
      );
    }

    const collaborators = data?.collaborators ?? [];
    if (collaborators.length === 0) {
      return (
        <tr>
          <td colSpan={5} className={styles.centerCell}>
            {_("empty_list")}
          </td>
        </tr>
      );
    }

    return collaborators.map((item) => (
      <tr key={item.uuid}>
        <td>{`${item.lastname ? item.lastname : "-"} ${
          item.firstname ? item.firstname : ""
        }`}</td>
        <td>{item.email}</td>
        <td className={styles.nowrap}>
          {item.phone && formatPhone(item.phone)}
        </td>
        <td>
          {["LEGAL_REPRESENTATIVE", "MANAGER"].includes(item.role?.role)
            ? "Admin"
            : _(item.role?.role)}
        </td>
        <td>{item.main_language}</td>
      </tr>
    ));
  };

  return (
    <Modal
      width="70vw"
      closable={false}
      visible={modalOpen}
      footer={null}
      onCancel={closeModal}
      maskClosable={false}
      destroyOnClose={true}
    >
      <div className={modalStyles.modal_header}>
        Collaborateurs
        <span className={modalStyles.modal_header_sub}>{fiduciary.name}</span>
      </div>
      <div className={modalStyles.modal_close} onClick={closeModal}>
        <i className="icon-ttp-close"></i>
      </div>

      <div className={modalStyles.content}>
        <div className={styles.inputSearch}>
          <span className={styles.inputSearch_icon}>
            <i className="icon-ttp-magnifier"></i>
          </span>
          <input
            type="text"
            placeholder={_("search")}
            onChange={(e) => debouncedSearch(e.target.value)}
          />
        </div>

        <table className={`${styles.table} unstriped`}>
          <thead>
            <tr>
              <th width="25%">Nom</th>
              <th width="25%">Email</th>
              <th>{_("phone_number")}</th>
              <th>{_("role")}</th>
              <th>{_("language")}</th>
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>

        {data?.total > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={data.total}
              pageSize={LIMIT}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>

      <div className={modalStyles.actions}>
        <Button variant="default" onClick={() => setModalOpen(false)}>
          {_("close")}
        </Button>
      </div>
    </Modal>
  );
};

export default CollaboratorsModal;
